import { AvailableLabel, Card, Container, Employee, Layouts } from "@/types/v2";
import { DataTable } from "@/components/Base/Datatable";
import { containerColumns } from "@/components/Layout/Containers.columns";
import { Button } from "@/components/Base/Button";
import { PencilIcon, TrashIcon } from "lucide-react";
import { ChangeEvent, Fragment, ReactNode, useEffect, useState } from "react";
import { ContainerDialog } from "@/components/Layout/ContainerDialog";
import {
  ContainerFormValues,
  toContainerFormValues,
} from "@/components/Layout/ContainerForm";
import { useConfirmDialog } from "@/components/Common/ConfirmDialog";
import { Tooltip } from "@/components/Common/Tooltip";
import { Spinner } from "@/components/Common/Spinner";
import { Input } from "@/components/Base/Input";
import { useTerminology } from "@/hooks/useTerminology";
import { RowSelectionState } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/Base/DropdownMenu";
import { AddToLayoutDialog } from "@/components/Layout/AddToLayoutDialog";
import { BulkLinkDialog } from "@/components/Layout/BulkLinkDialog";
import { useBulkLinkEditAvailability } from "@/hooks/useBulkLinkEditAvailability";
import { isBulkSelectable } from "@/lib/containerSelection";

interface ContainerTableProps {
  containers: Container[];
  employees: Employee[];
  cards: Card[];
  layouts: Layouts;
  labels: string[];
  labelCatalog?: AvailableLabel[];
  onEdit?: (container: Container) => Promise<void>;
  onDelete?: (container: Container) => Promise<void>;
  onAddToLayout?: (containerIds: string[], layoutName: string) => Promise<void>;
  showLinkedLayouts?: boolean;
  showCardCount?: boolean;
  showCompletedHours?: boolean;
  showTotalHours?: boolean;
  /**
   * Opt in to the bulk "Edit links…" action (WEBV3-337). Also requires
   * `useBulkLinkEditAvailability().enabled`. Off by default so read-only
   * report pages keep having no checkboxes.
   */
  showEditLinks?: boolean;
  integrationName?: string;
  technicianWritebackEnabled?: boolean;
}
export const ContainersTable = ({
  containers,
  employees,
  cards,
  layouts,
  labels,
  labelCatalog,
  onEdit,
  onDelete,
  onAddToLayout,
  showLinkedLayouts = true,
  showCardCount = false,
  showCompletedHours = false,
  showTotalHours = false,
  showEditLinks = false,
  integrationName,
  technicianWritebackEnabled,
}: ContainerTableProps) => {
  const [activeContainer, setActiveContainer] = useState<Container>();
  const [containerDialogOpen, setContainerDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchString, setSearchString] = useState("");
  const [searchedContainers, setSearchedContainers] = useState<Container[]>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [addToLayoutOpen, setAddToLayoutOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [editLinksOpen, setEditLinksOpen] = useState(false);
  const { terminology } = useTerminology();
  const { enabled: bulkLinkAvailable } = useBulkLinkEditAvailability();
  const editLinksAvailable = showEditLinks && bulkLinkAvailable;
  const hasBulkActions = !!(onDelete || onAddToLayout || editLinksAvailable);

  const { confirm, Dialog } = useConfirmDialog();

  const selectedContainers = searchedContainers.filter(
    (c) => rowSelection[c.id]
  );
  const isDeleteEligible = (container: Container) =>
    container.id !== "dispatch" && container.tags.length === 0;
  // The dialog skips dispatch itself (with a notice), so a mixed selection
  // still opens it; only a dispatch-only selection has nothing to edit.
  const canEditLinks = selectedContainers.some((c) => isBulkSelectable(c.id));

  const handleEdit = (container: Container) => {
    setActiveContainer(container);
    setContainerDialogOpen(true);
  };

  const handleDelete = async (container: Container) => {
    setActiveContainer(container);
    setIsSubmitting(true);
    try {
      await confirm({
        title: "Are you sure?",
        description: "This will delete this container. Do you wish to proceed?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setIsSubmitting(false);
      return;
    }

    if (onDelete) await onDelete(container);
    setActiveContainer(undefined);
    setIsSubmitting(false);
  };

  const handleBulkDelete = async () => {
    const eligible = selectedContainers.filter(isDeleteEligible);
    if (eligible.length === 0) return;

    const ineligibleCount = selectedContainers.length - eligible.length;
    setIsSubmitting(true);
    try {
      await confirm({
        title: "Are you sure?",
        description:
          ineligibleCount > 0
            ? `This will delete ${eligible.length} of ${selectedContainers.length} selected containers. ${ineligibleCount} container${ineligibleCount === 1 ? "" : "s"} can't be deleted (dispatch container or containers with cards) and will be skipped.`
            : `This will delete ${eligible.length} container${eligible.length === 1 ? "" : "s"}. Do you wish to proceed?`,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setIsSubmitting(false);
      return;
    }

    if (onDelete) {
      setIsBulkDeleting(true);
      try {
        for (const container of eligible) {
          await onDelete(container);
        }
      } catch (error) {
        console.error("Bulk delete failed partway through:", error);
      } finally {
        setIsBulkDeleting(false);
        setRowSelection({});
        setIsSubmitting(false);
      }
    } else {
      setRowSelection({});
      setIsSubmitting(false);
    }
  };

  const handleOpenAddToLayout = () => {
    // Radix's DropdownMenuItem `disabled` prop gates its own keyboard/select
    // handling, but not a caller-supplied `onClick` -- guard here too so a
    // click can't open the dialog when there's nothing to choose from.
    if (layouts.length === 0) return;
    setAddToLayoutOpen(true);
  };

  const handleAddToLayout = async (layoutName: string) => {
    if (!onAddToLayout) return;
    await onAddToLayout(
      selectedContainers.map((c) => c.id),
      layoutName
    );
    setRowSelection({});
  };

  const handleOpenEditLinks = () => {
    // Radix's `disabled` does not gate a caller-supplied `onClick`; guard here
    // too (same reason as handleOpenAddToLayout).
    if (!canEditLinks) return;
    setEditLinksOpen(true);
  };

  // BulkLinkDialog closes itself after an Apply settles; the selection was
  // the dialog's input, so drop it (mirrors handleAddToLayout).
  const handleLinksApplied = () => setRowSelection({});

  const actions = (container: Container) => {
    const actions: ReactNode[] = [];

    if (onEdit)
      actions.push(
        <Tooltip key={"update"} message={"Update this container."}>
          <Button
            onClick={() => handleEdit(container)}
            variant="ghost"
            id="delete_container"
            size="smIcon"
          >
            <PencilIcon size={20} />
          </Button>
        </Tooltip>
      );

    if (onDelete)
      actions.push(
        <Tooltip
          key={"delete"}
          message={
            container.id === "dispatch"
              ? "The dispatch container cannot be deleted"
              : container.tags.length > 0
                ? "Containers with cards cannot be deleted. Move the cards out of the container to delete"
                : "Delete this container. Note this cannot be undone."
          }
        >
          <Button
            onClick={() => handleDelete(container)}
            variant="destructive-alt"
            id="delete_container"
            size="smIcon"
            disabled={!isDeleteEligible(container)}
          >
            <TrashIcon size={20} />
          </Button>
        </Tooltip>
      );

    return actions;
  };

  const convertContainerData = (
    container: Container | undefined
  ): ContainerFormValues | undefined =>
    container ? toContainerFormValues(container) : undefined;

  const handleSubmitEdit = async (container: Partial<Container>) => {
    setIsSubmitting(true);
    if (onEdit) await onEdit(container as Container);
    setIsSubmitting(false);
    setActiveContainer(undefined);
    setContainerDialogOpen(false);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchString(e.target.value);
  };

  useEffect(() => {
    const query = searchString.trim().toLowerCase();
    if (query === "") {
      setSearchedContainers(containers);
    } else {
      setSearchedContainers(
        containers.filter(
          (container) =>
            container.id.toLowerCase().includes(query) ||
            container.displayName?.toLowerCase().includes(query)
        )
      );
    }
  }, [containers, searchString]);

  // If the rollout flag flips off mid-dialog the dialog unmounts; without this
  // `editLinksOpen` would stay true and re-pop it when the flag flips back.
  useEffect(() => {
    if (!editLinksAvailable) setEditLinksOpen(false);
  }, [editLinksAvailable]);

  return (
    <Fragment>
      {actions.length > 0 && <Dialog />}
      <ContainerDialog
        containerData={convertContainerData(activeContainer)}
        open={containerDialogOpen}
        employees={employees}
        onOpenChange={setContainerDialogOpen}
        onSubmit={handleSubmitEdit}
        labels={labels}
        labelCatalog={labelCatalog}
        externalTrigger
        loading={isSubmitting}
        containers={containers}
        integrationName={integrationName}
        technicianWritebackEnabled={technicianWritebackEnabled}
      />
      <div className="flex items-center justify-between">
        <Input
          className={"my-2 w-48 text-primary"}
          placeholder="Search containers"
          value={searchString}
          onChange={handleSearchChange}
        />
      </div>
      {hasBulkActions && selectedContainers.length > 0 && (
        <div className="flex items-center gap-2 py-2">
          <span className="text-sm font-medium">
            {selectedContainers.length} selected
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Actions</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {onDelete && (
                <DropdownMenuItem
                  disabled={!selectedContainers.some(isDeleteEligible)}
                  onClick={handleBulkDelete}
                >
                  Delete Selected
                </DropdownMenuItem>
              )}
              {onAddToLayout && (
                <DropdownMenuItem
                  disabled={layouts.length === 0}
                  onClick={handleOpenAddToLayout}
                >
                  Add to Layout
                </DropdownMenuItem>
              )}
              {editLinksAvailable && (
                <>
                  {(onDelete || onAddToLayout) && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    disabled={!canEditLinks}
                    onClick={handleOpenEditLinks}
                  >
                    Edit links…
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
      {onAddToLayout && (
        <AddToLayoutDialog
          open={addToLayoutOpen}
          onOpenChange={setAddToLayoutOpen}
          layoutNames={layouts.map((l) => Object.keys(l)[0])}
          containerCount={selectedContainers.length}
          onConfirm={handleAddToLayout}
        />
      )}
      {editLinksAvailable && (
        <BulkLinkDialog
          containerIds={selectedContainers.map((c) => c.id)}
          open={editLinksOpen}
          onOpenChange={setEditLinksOpen}
          onApplied={handleLinksApplied}
        />
      )}
      <div className="relative">
        {isBulkDeleting && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/70">
            <Spinner />
          </div>
        )}
        <DataTable
          data={searchedContainers}
          columns={containerColumns({
            actions: actions,
            loading: false,
            employees,
            layouts,
            cards,
            terminology,
          })}
          enableRowSelection={hasBulkActions}
          getRowId={(container) => container.id}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          userDefaults={{
            pagination: { pageSize: 10 },
            columnVisibility: {
              id: false,
              linkedLayouts: showLinkedLayouts,
              totalCards: showCardCount,
              totalHours: showTotalHours,
              completedHours: showCompletedHours,
            },
          }}
          factoryDefaults={{
            pagination: { pageSize: 10 },
            columnVisibility: {
              id: false,
              linkedLayouts: showLinkedLayouts,
              totalCards: showCardCount,
              totalHours: showTotalHours,
              completedHours: showCompletedHours,
            },
          }}
        />
      </div>
    </Fragment>
  );
};
